import {AfterViewInit, ChangeDetectorRef, Component, Inject, Input, OnInit, ViewChild} from '@angular/core';
import {
  FormControl,
  FormGroup, Validators,
} from '@angular/forms';
import {CardWebListElement} from '../../shared/CardWebListElement';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {CommunityConnectorService} from '../../shared/community-connector.service';
import {CommunityMember} from '../../shared/CommunityMember';
import {Observable, of, Subject} from 'rxjs';
import {catchError, map, shareReplay, startWith} from 'rxjs/operators';
import {CommunityMessageResponseElement} from '../../shared/community-message-response-element';
import {DesignInvitation} from '../../shared/DesignInvitation';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {CardWebRestService} from '../../shared/card-web-rest.service';

@Component({
  selector: 'em-share-dialog',
  templateUrl: './share-dialog.component.html',
  styleUrl: './share-dialog.component.css'
})

export class ShareDialogComponent implements OnInit, AfterViewInit {
  @Input() sharedDesign: CardWebListElement;

  loadingError$: Subject<boolean>;
  shareForm: FormGroup;
  ownCommunityMemberList$: Observable<CommunityMember[]>;
  allCommunityMemberList$: Observable<CommunityMember[]>;
  options = new Map<string, number>();
  filteredOptions: Observable<string[]>;
  username = new FormControl();
  message = new FormControl();
  shareOptions = new FormControl();
  isValid = false;
  messageSent: CommunityMessageResponseElement = undefined;
  private CACHE_SIZE = 1;
  readonly COMMUNITY_URL = 'https://community.empamos.de';

  displayedColumns: string[] = ['createdDateTime', 'receiverUsername', 'shareOption', 'accepted', 'delete'];
  tableData = [];
  dataSource = new MatTableDataSource<DesignInvitation>(this.tableData);
  isOnSharingTab = true;
  selectedTab = 0;

  paginator: MatPaginator;
  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.dataSource.paginator = this.paginator;
  }
  sort: MatSort;
  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.dataSource.sort = this.sort;
  }

  constructor(private ccs: CommunityConnectorService,
              private cws: CardWebRestService,
              private cdref: ChangeDetectorRef,
              @Inject(MAT_DIALOG_DATA) public data: { sharedDesign: CardWebListElement, selectedTab: number }
  ) {
    this.sharedDesign = data.sharedDesign;
    this.selectedTab = data.selectedTab;
    if (this.selectedTab > 0) {
      this.isOnSharingTab = false;
    }
    this.shareForm = new FormGroup({
      username: new FormControl('', [Validators.required]),
      message: new FormControl('', [Validators.maxLength(500)]),
      shareOptions: new FormControl('view', [Validators.required])
    });
    this.filteredOptions = this.username.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || '')),
    );
    this.messageSent = undefined;
    this.loadingError$ = new Subject<boolean>();
    this.shareOptions.setValue('view');
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    if (filterValue.trim() === '') {
      return [];
    }
    return Array.from(this.options.keys()).filter(username => username.toLowerCase().includes(filterValue));
  }

  ngOnInit(): void {
    this.ownCommunityMemberList$ = this.ccs.checkUserByMail().pipe(
      shareReplay(this.CACHE_SIZE),
      catchError((error) => {
        // it's important that we log an error here.
        // Otherwise you won't see an error in the console.
        console.log(error);
        this.loadingError$.next(true);
        return of<CommunityMember[]>();
      })
    );
    this.allCommunityMemberList$ = this.ccs.getAllUsers().pipe(
      shareReplay(this.CACHE_SIZE)
    );
    this.allCommunityMemberList$.subscribe( communityMemberList => {
      communityMemberList.forEach( communityMember => {
        this.options.set(communityMember.username, communityMember.id);

      });
    });
    this.username.valueChanges.subscribe(val => {
      this.isValid = Array.from(this.options.keys()).indexOf(this.username.value) !== -1;
    });
    this.fetchDesignInvitations();
  }

  ngAfterViewInit(): void {

  }

  fetchDesignInvitations() {
    this.ccs.getAllDesignInvitations(this.sharedDesign.name).subscribe(
      (dataObs: DesignInvitation[]) => {
        this.dataSource.data = dataObs;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        this.cdref.detectChanges();
      });
  }

  shareDesign(ownCommunityMember: CommunityMember) {
    const timestamp = Date.now().toString(); // is used as inivtation id in backend
    let messagePostText = "Du wurdest zu einem Design eingeladen. Um die Einladung anzunehmen, klicke auf den Link: <a href='https://app.empamos.de/home/invitation/" + timestamp + "'>https://app.empamos.de/home/invitation/" + timestamp + "</a>";
    messagePostText = messagePostText + "<br><br>Hinweis: Falls du im Safari-Browser unterwegs bist, kopiere den Link bitte in einen anderen Browser deiner Wahl, z.B. Firefox oder Chrome. Safari wird aktuell noch nicht unterstüzt."
    const messageText = this.message.value ? this.message.value + '<br/><br/>' + messagePostText : messagePostText;
    const returnValue = this.ccs.createDesignInvitation(
        ownCommunityMember.username,
        ownCommunityMember.id,
        'Einladung von ' + ownCommunityMember.username,
        messageText,
        this.username.value,
        this.options.get(this.username.value),
        this.sharedDesign.name,
        this.shareOptions.value,
        timestamp);
    returnValue.subscribe(communityMessageResponseElement => {
      if (communityMessageResponseElement !== null) {
        this.messageSent = communityMessageResponseElement;
        this.updateDesignDashboard();
        this.fetchDesignInvitations();
      }else{
        this.messageSent = {id: null, post_url: null};
      }
    });

  }

  decodeDesignName(name: string) {
    return decodeURIComponent(name);
  }

  shareAgain() {
    this.username.setValue('');
    this.messageSent = undefined;
  }

  getCommunityMessageUrl(postUrl: string) {
    return this.ccs.getCommunityMessageUrl(postUrl);
  }

  getCommunityMemberUrl(username: string) {
    return this.ccs.getCommunityMemberUrl(username);
  }

  deleteInvitation(designInvitation: DesignInvitation) {
    this.ccs.deleteDesignInvitation(designInvitation).subscribe(
      (dataObs: any) => {
        this.updateDesignDashboard();
        this.dataSource.data = dataObs;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        this.cdref.detectChanges();
        if (this.dataSource.data.length === 0) {
          this.selectedTab = 0;
        }
      });
  }

  onTabChange($event: MatTabChangeEvent) {
    if ($event.index === 0) {
      this.isOnSharingTab = true;
    }else if ($event.index === 1) {
      this.isOnSharingTab = false;
    }
  }

  updateDesignDashboard() {
      this.cws.emitDesignInvitationChangeEvent();
  }
}
